import { useId } from 'react';
import { METALS, parsePairSymbol } from '@pipyar/core';
import { cn } from '@/lib/utils';

/**
 * Colour per currency.
 *
 * Chosen so the majors are distinguishable side by side rather than for any
 * national association — these end up as 20px discs in a list, where hue is the
 * only thing carrying information.
 */
const CURRENCY_COLORS: Record<string, string> = {
  USD: '#2f9e6b',
  EUR: '#3b6fd4',
  GBP: '#7c4dbd',
  JPY: '#d4483b',
  CHF: '#d43b5e',
  AUD: '#e08a2e',
  NZD: '#2e9fa8',
  CAD: '#d4553b',
  CNH: '#c4392e',
  CNY: '#c4392e',
  SEK: '#3f7cc4',
  NOK: '#4a5fc4',
  DKK: '#c4453b',
  PLN: '#c43b6b',
  TRY: '#c43b3b',
  ZAR: '#3d9e4a',
  MXN: '#2e8f5e',
  SGD: '#c43b4f',
  HKD: '#c4483b',
  // Metals get their actual metal colour — the one case where the obvious
  // choice is also the most legible.
  XAU: '#d4a017',
  XAG: '#9ca6b0',
  XPT: '#8fa3ad',
  XPD: '#a89b8c',
};

/**
 * Deterministic fallback for a code with no assigned colour.
 *
 * Hashing the letters means an exotic pair still gets a stable, distinct hue
 * instead of everything unknown collapsing to one grey.
 */
function colorFor(code: string): string {
  const known = CURRENCY_COLORS[code];
  if (known) return known;

  let hash = 0;
  for (let i = 0; i < code.length; i++) hash = (hash * 31 + code.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 45% 45%)`;
}

interface PairIconProps {
  symbol: string;
  size?: number;
  className?: string;
}

/**
 * Icon for a trading pair, drawn from the symbol itself.
 *
 * Generated rather than shipped as assets: pairs come from whatever the broker
 * exports, so a fixed icon set would be missing exactly the symbol that was just
 * added. Splitting a disc between the base and quote currency colours gives every
 * pair a stable identity with no files to maintain.
 *
 * Metals are drawn as a solid disc with an ingot glyph instead of a split. Gold
 * is the primary instrument here and reads better as one recognisable object
 * than as half a bicolour circle.
 */
export function PairIcon({ symbol, size = 22, className }: PairIconProps) {
  const parsed = parsePairSymbol(symbol);
  // useId keeps the clipPath unique when several icons share a page; a fixed id
  // would make every instance clip to whichever rendered last.
  const clipId = useId();

  const label = parsed.recognised ? `${parsed.base}/${parsed.quote}` : symbol;

  if (!parsed.recognised) {
    // Unknown shape (an index, a CFD): initials on a hashed background.
    const initials = symbol.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={cn('shrink-0', className)}
        role="img"
        aria-label={label}
      >
        <circle cx="12" cy="12" r="12" fill={colorFor(symbol.toUpperCase())} />
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="9.5"
          fontWeight="700"
          fill="#fff"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
          {initials}
        </text>
      </svg>
    );
  }

  if (parsed.isMetal) {
    const metal = colorFor(parsed.base);
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={cn('shrink-0', className)}
        role="img"
        aria-label={`${METALS[parsed.base] ?? parsed.base} vs ${parsed.quote}`}
      >
        <circle cx="12" cy="12" r="12" fill={metal} />
        {/* A stack of two ingots, viewed end-on. */}
        <g fill="#fff" fillOpacity="0.92">
          <path d="M6.4 13.6h11.2l1 3.2H5.4z" />
          <path d="M8.2 8.6h7.6l0.9 3.1H7.3z" fillOpacity="0.7" />
        </g>
        {/* Quote currency as a thin arc, so XAUUSD and XAUEUR differ visibly. */}
        <circle
          cx="12"
          cy="12"
          r="11"
          fill="none"
          stroke={colorFor(parsed.quote)}
          strokeWidth="2"
          strokeDasharray="17 52"
          strokeLinecap="round"
          transform="rotate(-45 12 12)"
        />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn('shrink-0', className)}
      role="img"
      aria-label={label}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="12" cy="12" r="12" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* Base fills the disc; quote covers the lower-right half on a diagonal,
            so the base currency reads first — which is how the pair is named. */}
        <rect x="0" y="0" width="24" height="24" fill={colorFor(parsed.base)} />
        <path d="M24 0v24H0z" fill={colorFor(parsed.quote)} />
        <path d="M24 0L0 24" stroke="#1b1d24" strokeOpacity="0.35" strokeWidth="1.5" />
      </g>
    </svg>
  );
}
